import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Timeoff } from '../types/entities';

interface TimeoffInfoDialogProps {
  open: boolean;
  onClose: () => void;
  timeoff: Timeoff | null;
}

const TimeoffInfoDialog: React.FC<TimeoffInfoDialogProps> = ({
  open,
  onClose,
  timeoff,
}) => {
  const { t } = useTranslation();

  if (!timeoff) return null;

  const formatDateTime = (date: Date) => {
    return new Date(date).toLocaleString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const calculateDuration = (startDate: Date, endDate: Date) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffHours < 24) {
      return { hours: diffHours, isPartial: true, days: undefined };
    } else {
      return { days: diffDays, isPartial: false, hours: undefined };
    }
  };

  const duration = calculateDuration(timeoff.startDate, timeoff.endDate);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('timeoffs.timeoffDetails')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('timeoffs.basicInformation')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('common.hairdresser')}:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {timeoff.hairdresser?.name || t('common.notAvailable')}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('timeoffs.startDate')}:
                </Typography>
                <Typography variant="body2">
                  {formatDateTime(timeoff.startDate)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('timeoffs.endDate')}:
                </Typography>
                <Typography variant="body2">
                  {formatDateTime(timeoff.endDate)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('timeoffs.duration')}:
                </Typography>
                {duration.isPartial ? (
                  <Chip
                    label={`${duration.hours} ${t('timeoffs.hours')}`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                ) : (
                  <Chip
                    label={`${duration.days} ${(duration.days || 0) === 1 ? t('timeoffs.day') : t('timeoffs.days')}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          </Box>

          {timeoff.description && (
            <>
              <Divider />
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('timeoffs.timeoffDescription')}
                </Typography>
                <Typography variant="body2">
                  {timeoff.description}
                </Typography>
              </Box>
            </>
          )}

          <Divider />
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('common.metadata')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('timeoffs.created')}:
                </Typography>
                <Typography variant="body2">
                  {formatDateTime(timeoff.createdAt)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('timeoffs.lastUpdated')}:
                </Typography>
                <Typography variant="body2">
                  {formatDateTime(timeoff.updatedAt)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TimeoffInfoDialog;
